import { useCallback, useEffect, useRef, useState } from "react";
import { $isLinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { mergeRegister } from "@lexical/utils";
import {
	SELECTION_CHANGE_COMMAND,
	$getSelection,
	$isRangeSelection,
} from "lexical";
import { createPortal } from "react-dom";

import { Button } from "components";
import { getSelectedNode } from "components/editor/helpers/getSelectedNode";

import { FloatingLinkEditor } from "./FloatingLinkEditor";

import { ReactComponent as LinkIcon } from "static/images/link.svg";
import styles from "../../editor.module.scss";

const LowPriority = 1;

export default function ToolbarPlugin({ editable }) {
	const [editor] = useLexicalComposerContext();
	const toolbarRef = useRef(null);

	const [isLink, setIsLink] = useState(false);

	const updateToolbar = useCallback(() => {
		const selection = $getSelection();
		if ($isRangeSelection(selection)) {
			// Update links
			const node = getSelectedNode(selection);
			const parent = node.getParent();
			if ($isLinkNode(parent) || $isLinkNode(node)) {
				setIsLink(true);
			} else {
				setIsLink(false);
			}
		}
	}, []);

	useEffect(() => {
		return mergeRegister(
			editor.registerUpdateListener(({ editorState }) => {
				editorState.read(() => {
					updateToolbar();
				});
			}),
			editor.registerCommand(
				SELECTION_CHANGE_COMMAND,
				(_payload, newEditor) => {
					updateToolbar();
					return false;
				},
				LowPriority,
			),
		);
	}, [editor, updateToolbar]);

	const insertLink = useCallback(() => {
		if (!isLink) {
			editor.dispatchCommand(TOGGLE_LINK_COMMAND, "https://");
		} else {
			editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
		}
	}, [editor, isLink]);

	return (
		<div className={styles.toolbar} ref={toolbarRef}>
			<Button
				onClick={insertLink}
				icon={<LinkIcon />}
				btnIcon
				variant="secondary"
				size="sm"
				className={`${styles.toolbarItem} ${styles.spaced} ${
					isLink ? `${styles.active}` : ""
				}`}
				disabled={!editable}
			/>

			{isLink &&
				createPortal(
					<FloatingLinkEditor editor={editor} />,
					document.body,
				)}
		</div>
	);
}
